/**
 * Modos de renderização quando o aspect ratio do conteúdo
 * (design 1920x1080 ou 1080x1920) não bate com o viewport real do device.
 *
 * - `contain`: Math.min — preserva aspect, deixa barras pretas (letterbox).
 * - `cover`:   Math.max — preserva aspect, corta laterais ou topo/base.
 * - `stretch`: scaleX != scaleY até MAX_DEFORMATION_PCT, depois clamp + letterbox
 *              no excedente. Combina com `object-fit: fill` no Stream.
 *
 * Override automático: Android + kiosk OFF → sempre `contain` (operador precisa
 * ver status/nav bars do Android pra acessar Settings/Wi-Fi/Factory Reset).
 */
export type FitMode = 'contain' | 'cover' | 'stretch';

/**
 * Deformação máxima permitida em `stretch` mode. Acima disso, faz clamp
 * (estica até X%, resto vira letterbox).
 *
 * 10% é o sweet spot empírico:
 * - Tab A7 SM-T505 (mismatch 0.27%) → estica 100% imperceptível.
 * - Browser ultrawide (mismatch 26%) → clamp em 10%, ~30% menos letterbox vs contain puro.
 * - Acima disso (>10%) → texto começa a parecer esmagado.
 */
export const MAX_DEFORMATION_PCT = 10;

const VALID_MODES: readonly FitMode[] = [
  'contain',
  'cover',
  'stretch',
] as const;

/**
 * Feature flag build-time (Vite env). Define o modo de renderização default
 * quando o player não recebe override programático.
 *
 * **Builds disponíveis (Fase 2 do roadmap de renderização):**
 *
 * ```bash
 * # Default (stretch) — sem env
 * ./scripts/build-tenant.sh efflux android release
 *
 * # Cover (corta laterais — comportamento v1.0.189)
 * VITE_RENDER_FIT_MODE=cover ./scripts/build-tenant.sh efflux android release
 *
 * # Stretch explícito (estica até 10%, depois letterbox)
 * VITE_RENDER_FIT_MODE=stretch ./scripts/build-tenant.sh efflux android release
 *
 * # Contain (centraliza com barras pretas)
 * VITE_RENDER_FIT_MODE=contain ./scripts/build-tenant.sh efflux android release
 * ```
 *
 * **Arquitetura "pluggable" (futuro):**
 *
 * Quando o admin tiver UI de `tab_tenants.fit_mode` + `tab_players.fit_mode`,
 * o server vai enviar `playerSettings.fitMode` no sync feed. O store reativo
 * `playerSettingsStore.fitMode` (a criar) vai sobrescrever esta flag via
 * parâmetro `overrideMode` do `useViewportScale`. Esta env passa a ser o
 * fallback. **Nenhuma mudança neste arquivo necessária — só adicionar o
 * consumer do store no ScaledViewport.**
 *
 * @default 'stretch' — preenche tela sem letterbox em mismatches pequenos.
 */
export const ENV_FIT_MODE: FitMode = (() => {
  const value = import.meta.env.VITE_RENDER_FIT_MODE;
  if (typeof value === 'string' && VALID_MODES.includes(value as FitMode)) {
    return value as FitMode;
  }
  return 'stretch';
})();
